import { useEffect, useState, useCallback } from "react";
import {
  Loader2, UserPlus, MoreHorizontal, Ship, Mail, Copy, ShieldCheck,
  UserCog, Ban, RotateCcw, Trash2, Send, KeyRound, BarChart3,
} from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invited: "bg-amber-50 text-amber-700 border-amber-200",
  deactivated: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => toast.success("Copied"),
        () => toast.info(text),
      );
      return;
    }
  } catch { /* fall through */ }
  toast.info(text);
}

export default function SupplierTeam() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [yachts, setYachts] = useState([]);
  const [activity, setActivity] = useState({});
  const [workload, setWorkload] = useState({});
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // member being assigned yachts
  const [confirm, setConfirm] = useState(null);     // {member, action}
  const [permFor, setPermFor] = useState(null);     // member whose permissions are edited
  const [perfOpen, setPerfOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.supplier_role === "admin";
  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const [t, y] = await Promise.all([
        api.get("/supplier/team"),
        api.get("/supplier/team/client-yachts"),
      ]);
      setData(t.data);
      setYachts(y.data || []);
      api.get("/supplier/team/activity")
        .then(({ data: a }) => setActivity(Object.fromEntries((a || []).map((r) => [r.member_id, r]))))
        .catch(() => {});
      api.get("/supplier/team/workload")
        .then(({ data: w }) => setWorkload(Object.fromEntries((w || []).map((r) => [r.member_id, r.open_jobs]))))
        .catch(() => {});
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const act = async (member, body, okMsg) => {
    setBusy(true);
    try {
      await api.patch(`/supplier/team/${member.id}`, body);
      toast.success(okMsg);
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); setConfirm(null); }
  };

  const remove = async (member) => {
    setBusy(true);
    try {
      await api.delete(`/supplier/team/${member.id}`);
      toast.success(member.status === "invited" ? "Invite revoked" : "Member removed");
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); setConfirm(null); }
  };

  const resend = async (member) => {
    try {
      const { data: r } = await api.post(`/supplier/team/${member.id}/resend`);
      toast.success("Invite email sent again");
      if (r.invite_link) copyText(r.invite_link);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500 p-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading team…</div>;
  }
  if (user?.supplier_role && user.supplier_role !== "admin") {
    return (
      <div className="p-6 lg:p-10 max-w-xl" data-testid="team-no-access">
        <h1 className="font-display text-3xl mb-3">Team</h1>
        <div className="rounded-xl border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500">
          Only a company admin can manage the team. Ask your admin if you need a change to your yacht assignments.
        </div>
      </div>
    );
  }
  if (!data) return null;

  const yachtName = (id) => yachts.find((y) => y.id === id)?.name || id;

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="supplier-team-page">
      <div className="mb-2 text-xs uppercase tracking-wide text-zinc-400">{data.company_name}</div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl">Team</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPerfOpen(true)} data-testid="team-performance-btn">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Performance
          </Button>
          <Button onClick={() => setInviteOpen(true)} className="bg-ink hover:bg-black" data-testid="invite-manager-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Invite team
          </Button>
        </div>
      </div>
      <p className="text-sm text-zinc-500 mb-5">
        Account managers handle quotes, jobs, invoices and owner messages — but only for the yachts you assign to them. Admins see everything.
      </p>

      <div className="space-y-3">
        {data.members.map((m) => (
          <div key={m.id} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`team-member-${m.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {m.picture && <img src={m.picture} alt="" className="h-7 w-7 rounded-full object-cover border border-zinc-200" data-testid={`member-photo-${m.id}`} />}
                  <span className="font-medium">{m.name || m.email}</span>
                  <Badge className={m.role === "admin" ? "bg-ink text-white" : "bg-sky-50 text-sky-700 border border-sky-200"} data-testid={`member-role-${m.id}`}>
                    {m.role === "admin" ? "Admin" : "Manager"}
                  </Badge>
                  <Badge className={`border ${STATUS_STYLE[m.status] || ""}`} data-testid={`member-status-${m.id}`}>
                    {m.status === "invited" ? "Invited" : m.status === "deactivated" ? "Deactivated" : "Active"}
                  </Badge>
                  {workload[m.id] != null && m.status === "active" && (
                    <Badge variant="secondary" className="text-[10px]" data-testid={`member-open-${m.id}`}>
                      {workload[m.id]} open {workload[m.id] === 1 ? "task" : "tasks"}
                    </Badge>
                  )}
                  {m.is_owner && <span className="text-xs text-zinc-400">(your company account)</span>}
                  {m.role === "manager" && m.permissions?.can_quote === false && (
                    <Badge className="bg-amber-50 text-amber-700 border border-amber-200" data-testid={`perm-no-quote-${m.id}`}>No offers</Badge>
                  )}
                  {m.role === "manager" && m.permissions?.can_invoice === false && (
                    <Badge className="bg-amber-50 text-amber-700 border border-amber-200" data-testid={`perm-no-invoice-${m.id}`}>No invoicing</Badge>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500">
                  <Mail className="h-3.5 w-3.5" /> {m.email}
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-zinc-500 flex-wrap">
                  <Ship className="h-3.5 w-3.5 shrink-0" />
                  {m.yacht_ids === null || m.role === "admin"
                    ? <span>All client yachts</span>
                    : (m.yacht_ids || []).length === 0
                      ? <span className="text-amber-600">No yachts assigned yet</span>
                      : (m.yacht_ids || []).map((id) => (
                        <Badge key={id} variant="outline" className="font-normal">{yachtName(id)}</Badge>
                      ))}
                </div>
                <ActivityStrip stats={activity[m.id]} />
              </div>
              {!m.is_owner && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8" data-testid={`member-actions-${m.id}`}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {m.status === "invited" && (
                      <>
                        <DropdownMenuItem onClick={() => resend(m)} data-testid={`resend-invite-${m.id}`}>
                          <Send className="h-4 w-4 mr-2" /> Resend invite
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirm({ member: m, action: "revoke" })} className="text-red-600" data-testid={`revoke-invite-${m.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Revoke invite
                        </DropdownMenuItem>
                      </>
                    )}
                    {m.status !== "invited" && (
                      <>
                        {m.role === "manager" && (
                          <DropdownMenuItem onClick={() => setAssignFor(m)} data-testid={`assign-yachts-${m.id}`}>
                            <Ship className="h-4 w-4 mr-2" /> Assign yachts
                          </DropdownMenuItem>
                        )}
                        {m.role === "manager" && (
                          <DropdownMenuItem onClick={() => setPermFor(m)} data-testid={`permissions-${m.id}`}>
                            <KeyRound className="h-4 w-4 mr-2" /> Permissions
                          </DropdownMenuItem>
                        )}
                        {m.role === "manager"
                          ? (
                            <DropdownMenuItem onClick={() => act(m, { role: "admin" }, "Promoted to admin")} data-testid={`promote-${m.id}`}>
                              <ShieldCheck className="h-4 w-4 mr-2" /> Promote to admin
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => act(m, { role: "manager" }, "Changed to manager")} data-testid={`demote-${m.id}`}>
                              <UserCog className="h-4 w-4 mr-2" /> Change to manager
                            </DropdownMenuItem>
                          )}
                        <DropdownMenuSeparator />
                        {m.status === "active" ? (
                          <DropdownMenuItem onClick={() => setConfirm({ member: m, action: "deactivate" })} data-testid={`deactivate-${m.id}`}>
                            <Ban className="h-4 w-4 mr-2" /> Deactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => act(m, { action: "reactivate" }, "Member reactivated")} data-testid={`reactivate-${m.id}`}>
                            <RotateCcw className="h-4 w-4 mr-2" /> Reactivate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setConfirm({ member: m, action: "remove" })} className="text-red-600" data-testid={`remove-${m.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Remove permanently
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        ))}
      </div>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={load} />

      {assignFor && (
        <AssignYachtsDialog
          member={assignFor}
          yachts={yachts}
          busy={busy}
          onClose={() => setAssignFor(null)}
          onSave={(ids) => act(assignFor, { yacht_ids: ids }, "Yacht assignments updated").then(() => setAssignFor(null))}
        />
      )}

      {permFor && (
        <PermissionsDialog
          member={permFor}
          busy={busy}
          onClose={() => setPermFor(null)}
          onSave={(perms) => act(permFor, { permissions: perms }, "Permissions updated").then(() => setPermFor(null))}
        />
      )}

      {perfOpen && (
        <PerformanceDialog members={data.members} onClose={() => setPerfOpen(false)} />
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirm?.action === "revoke" && "Revoke this invite?"}
              {confirm?.action === "deactivate" && "Deactivate this member?"}
              {confirm?.action === "remove" && "Remove this member permanently?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.action === "revoke" && "The invite link stops working immediately."}
              {confirm?.action === "deactivate" && "Their login is blocked immediately. Their history (messages, completed jobs) stays intact. You can reactivate them later."}
              {confirm?.action === "remove" && "Their login is blocked and they disappear from the team. Their history stays intact. This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          {confirm?.action === "deactivate" && (confirm.member.yacht_ids || []).length > 0 && (() => {
            const colleagues = (data.members || []).filter(
              (x) => !x.is_owner && x.id !== confirm.member.id && x.status === "active" && x.role === "manager",
            );
            return (
              <div className="space-y-1.5">
                <Label>Hand their yachts over to</Label>
                <select
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="w-full h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid="transfer-to-select"
                >
                  <option value="">No handover — yachts fall back to admins</option>
                  {colleagues.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.email}</option>
                  ))}
                </select>
                {colleagues.length === 0 && (
                  <p className="text-xs text-zinc-400">No other active managers — their yachts will be handled by admins.</p>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="confirm-cancel-btn">Cancel</Button>
            <Button
              className={confirm?.action === "deactivate" ? "bg-ink hover:bg-black" : "bg-red-600 hover:bg-red-700"}
              disabled={busy}
              data-testid="confirm-action-btn"
              onClick={() => {
                if (confirm.action === "deactivate") {
                  act(confirm.member, { action: "deactivate", ...(transferTo ? { transfer_to: transferTo } : {}) },
                    transferTo ? "Member deactivated — yachts handed over" : "Member deactivated");
                  setTransferTo("");
                } else remove(confirm.member);
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {confirm?.action === "deactivate" ? "Deactivate" : confirm?.action === "revoke" ? "Revoke" : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActivityStrip({ stats }) {
  if (!stats) return null;
  const items = [
    ["Offers sent", stats.offers_sent],
    ["Jobs completed", stats.jobs_completed],
    ["Messages", stats.messages_sent],
    ["Avg response", stats.avg_response_hours != null ? `${stats.avg_response_hours}h` : "—"],
  ];
  return (
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-500" data-testid={`member-activity-${stats.member_id}`}>
      {items.map(([k, v]) => (
        <span key={k}><span className="text-zinc-400">{k}</span> · <span className="font-medium text-zinc-700">{v}</span></span>
      ))}
    </div>
  );
}

function PermissionsDialog({ member, busy, onClose, onSave }) {
  const p = member.permissions || {};
  const [canQuote, setCanQuote] = useState(p.can_quote !== false);
  const [canInvoice, setCanInvoice] = useState(p.can_invoice !== false);
  const [invoiceScope, setInvoiceScope] = useState(p.invoice_scope === "own" ? "own" : "all");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Permissions for {member.name || member.email}</DialogTitle>
          <DialogDescription>
            Restrict what this manager may do on their assigned yachts. Chatting with owners is always allowed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 px-3 py-3 cursor-pointer">
            <div>
              <div className="text-sm font-medium">May send offers</div>
              <div className="text-xs text-zinc-500">Submit, edit and negotiate quotes</div>
            </div>
            <Switch checked={canQuote} onCheckedChange={setCanQuote} data-testid="perm-quote-switch" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 px-3 py-3 cursor-pointer">
            <div>
              <div className="text-sm font-medium">May manage invoices</div>
              <div className="text-xs text-zinc-500">Edit invoices, send them, confirm payments and request prepayments</div>
            </div>
            <Switch checked={canInvoice} onCheckedChange={setCanInvoice} data-testid="perm-invoice-switch" />
          </label>
          {canInvoice && (
            <div className="rounded-lg border border-zinc-200 px-3 py-3 space-y-2">
              <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Invoice visibility</div>
              {[
                ["all", "May manage all invoices", "Sees and handles every invoice of the company"],
                ["own", "May manage own invoices", "Sees only invoices of the jobs they handle"],
              ].map(([val, label, desc]) => (
                <label key={val} className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    name="invoice-scope"
                    checked={invoiceScope === val}
                    onChange={() => setInvoiceScope(val)}
                    className="mt-0.5 accent-black"
                    data-testid={`perm-invoice-scope-${val}`}
                  />
                  <span>
                    <span className="block text-sm font-medium">{label}</span>
                    <span className="block text-xs text-zinc-500">{desc}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="perm-cancel-btn">Cancel</Button>
          <Button onClick={() => onSave({ can_quote: canQuote, can_invoice: canInvoice, invoice_scope: invoiceScope })} disabled={busy} className="bg-ink hover:bg-black" data-testid="perm-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PerformanceDialog({ members, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/supplier/team/activity", { params: { months: 6 } })
      .then(({ data }) => setRows(data || []))
      .catch((e) => { toast.error(fmtErr(e)); setRows([]); });
  }, []);
  const nameFor = (id) => members.find((m) => m.id === id)?.name
    || members.find((m) => m.id === id)?.email || id;
  const monthKeys = rows?.[0]?.monthly?.map((x) => x.month) || [];
  const fmtMonth = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString(undefined, { month: "short" });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Team performance — last 6 months</DialogTitle>
          <DialogDescription>Offers sent / jobs completed per member and month.</DialogDescription>
        </DialogHeader>
        {!rows ? (
          <div className="flex items-center gap-2 text-zinc-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="overflow-x-auto" data-testid="team-performance-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-400 border-b border-zinc-100">
                  <th className="py-2 pr-3 font-medium">Member</th>
                  {monthKeys.map((k) => <th key={k} className="py-2 px-2 font-medium text-center">{fmtMonth(k)}</th>)}
                  <th className="py-2 pl-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.member_id} className="border-b border-zinc-50">
                    <td className="py-2 pr-3 font-medium whitespace-nowrap">{nameFor(r.member_id)}</td>
                    {(r.monthly || []).map((mo) => (
                      <td key={mo.month} className="py-2 px-2 text-center text-zinc-600">
                        {mo.offers || mo.jobs_completed ? `${mo.offers} / ${mo.jobs_completed}` : "·"}
                      </td>
                    ))}
                    <td className="py-2 pl-2 text-right text-zinc-700 whitespace-nowrap">{r.offers_sent} / {r.jobs_completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-zinc-400">offers sent / jobs completed · avg response time on the member card</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="performance-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ open, onClose, onInvited }) {
  const [mode, setMode] = useState("invite"); // "invite" | "create"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [created, setCreated] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "create") {
        await api.post("/supplier/team/create", { name, email, password });
        toast.success("Account created");
        setCreated(true);
      } else {
        const { data } = await api.post("/supplier/team/invite", { name, email });
        toast.success("Invite sent");
        setLink(data.invite_link || "");
      }
      onInvited();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const close = () => { setMode("invite"); setName(""); setEmail(""); setPassword(""); setLink(""); setCreated(false); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add an account manager</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Create their account yourself and hand them the credentials — they can sign in right away."
              : "They'll get an email with a secure link to set their own password. The link expires in 7 days."}
          </DialogDescription>
        </DialogHeader>
        {link || created ? (
          <div className="space-y-3">
            {created ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="create-success-note">
                Account for <strong>{email}</strong> is ready — share the password with them privately. They can change it later under Account preferences.
              </div>
            ) : (
              <>
                <p className="text-sm text-zinc-600">Invite sent. If the email doesn't arrive, share this link manually:</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={link} className="text-xs" data-testid="invite-link-field" />
                  <Button variant="outline" size="icon" onClick={() => copyText(link)} data-testid="copy-invite-link-btn">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
            <DialogFooter>
              <Button onClick={close} className="bg-ink hover:bg-black" data-testid="invite-done-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-zinc-100 p-1 text-sm">
              <button
                type="button"
                onClick={() => setMode("invite")}
                className={`rounded-md py-1.5 transition-colors ${mode === "invite" ? "bg-white shadow-sm font-medium" : "text-zinc-500"}`}
                data-testid="mode-invite-btn"
              >Send email invite</button>
              <button
                type="button"
                onClick={() => setMode("create")}
                className={`rounded-md py-1.5 transition-colors ${mode === "create" ? "bg-white shadow-sm font-medium" : "text-zinc-500"}`}
                data-testid="mode-create-btn"
              >Create with password</button>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="inv-name">Name</Label>
                <Input id="inv-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" data-testid="invite-name-input" />
              </div>
              <div>
                <Label htmlFor="inv-email">Email</Label>
                <Input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@company.com" data-testid="invite-email-input" />
              </div>
              {mode === "create" && (
                <div>
                  <Label htmlFor="inv-pass">Password</Label>
                  <Input id="inv-pass" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="invite-password-input" />
                  <p className="mt-1 text-[11px] text-zinc-400">Shown in plain text so you can copy it — share it privately.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="invite-cancel-btn">Cancel</Button>
              <Button
                onClick={submit}
                disabled={busy || !name.trim() || !email.trim() || (mode === "create" && password.length < 8)}
                className="bg-ink hover:bg-black"
                data-testid="invite-send-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : mode === "create" ? <UserPlus className="h-4 w-4 mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                {mode === "create" ? "Create account" : "Send invite"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssignYachtsDialog({ member, yachts, busy, onClose, onSave }) {
  const [sel, setSel] = useState(new Set(member.yacht_ids || []));
  const [q, setQ] = useState("");
  const toggle = (id) => setSel((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const shown = q.trim()
    ? yachts.filter((y) => (y.name || "").toLowerCase().includes(q.trim().toLowerCase()))
    : yachts;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign yachts to {member.name || member.email}</DialogTitle>
          <DialogDescription>
            They'll only see tasks, offers, invoices and messages for the selected yachts.
          </DialogDescription>
        </DialogHeader>
        {yachts.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4">
            No client yachts yet — a yacht becomes assignable once your company has a job, quote or invoice with it.
          </p>
        ) : (
          <>
            {yachts.length > 7 && (
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search yachts…" data-testid="assign-yacht-search" />
            )}
            <div className="max-h-72 overflow-y-auto space-y-1 py-1">
              {shown.length === 0 && <p className="text-sm text-zinc-400 py-3 text-center">No yachts match "{q}"</p>}
              {shown.map((y) => (
                <label key={y.id} className="flex items-center gap-3 rounded-lg border border-zinc-200 px-3 py-2.5 cursor-pointer hover:bg-zinc-50" data-testid={`assign-yacht-${y.id}`}>
                  <Checkbox checked={sel.has(y.id)} onCheckedChange={() => toggle(y.id)} />
                  <span className="text-sm">{y.name}</span>
                </label>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="assign-cancel-btn">Cancel</Button>
          <Button onClick={() => onSave([...sel])} disabled={busy} className="bg-ink hover:bg-black" data-testid="assign-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save assignments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
